import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../firebase";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../../redux/actions/loadingActions";
import { getUserRole } from "../../utils/getUserRole";
import { clearAuthBlocked, setAuthBlocked, setUserRole } from "../../../redux/actions/authActions";
import { mostrarAviso, mostrarError, mostrarInfo } from "../../utils/Alertas";
import { TextField } from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";

interface Props {
    onSuccess: () => void;
}

type CheckLoginBlockResponse = {
	blocked: boolean;
	warning: boolean;
};

const LoginForm: React.FC<Props> = ({ onSuccess }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showResetHint, setShowResetHint] = useState(false);
    const dispatch = useDispatch();

    // Declaraciones de funciones
    const functions = getFunctions();
    const checkUserApprovalFn = httpsCallable(functions, "checkUserApproval");
    const checkLoginBlockFn = httpsCallable<{ email: string }, CheckLoginBlockResponse>(functions, "checkLoginBlock");
    const registerLoginSuccessFn = httpsCallable(functions, "registerLoginSuccess");
    const registerLoginFailureFn = httpsCallable(functions, "registerLoginFailure");
    const requestPasswordResetFn = httpsCallable<{ email: string }, { ok: boolean }>(functions,	"requestPasswordReset");

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !password) {
            if (!email && !password) {
                mostrarInfo("Por favor, ingresa tu correo y tu contraseña.");
            } else if (!email) {
                mostrarInfo("Por favor, ingresa tu correo electrónico.");
            } else {
                mostrarInfo("Por favor, ingresa tu contraseña.");
            }
            return;
        }

        dispatch(showLoading());
        dispatch(setAuthBlocked(true));

        try {
            // 0️⃣ Comprobar bloqueo previo
            try {
                const res = await checkLoginBlockFn({ email });

                if (res.data.blocked) {
                    mostrarAviso("Demasiados intentos fallidos. Si has olvidado tu contraseña, restablécela. Si ya la has cambiado, puedes intentar entrar.");
                    return;
                }

                if (res.data.warning) {
                    setShowResetHint(true);
                } else {
                    setShowResetHint(false);
                }
            } catch {
                // Si falla la comprobación, no bloqueamos el login
            }

            // 1️⃣ Intento de autenticación
            await signInWithEmailAndPassword(auth, email, password);

            // 2️⃣ Registrar login correcto (limpia bloqueos)
            try {
                await registerLoginSuccessFn({ email });
            } catch {
                // No bloquea el login si falla el log
            }

            // 2️⃣ Verificación de aprobación
            try {
                await checkUserApprovalFn();
            } catch (err: any) {
                await auth.signOut();

                // Mensajes según motivo
                const code = err?.code;

                if (code === "functions/permission-denied") {
                    mostrarInfo(
                        "Tu cuenta está pendiente de aprobación por un administrador.",
                    );
                } else if (code === "functions/unauthenticated") {
                    mostrarError("La sesión no es válida. Inténtalo de nuevo.");
                } else {
                    mostrarError(
                        "No se pudo verificar el estado de tu cuenta.",
                    );
                }
                return;
            }

            // 3️⃣ Obtener rol
            const rol = await getUserRole();
            if (rol) {
                dispatch(setUserRole(rol));
            }

            // 4️⃣ Login correcto
            onSuccess();
            setShowResetHint(false);
        } catch (err: any) {
            // 🔐 Errores de Firebase Auth
            const code = err?.code;

            // 🔐 Errores de credenciales → registrar fallo
            if (
                code === "auth/user-not-found" ||
                code === "auth/wrong-password" ||
                code === "auth/invalid-credential"
            ) {
                try {
                    await registerLoginFailureFn({ email });
                } catch {
                    // Ignorar errores de logging
                }

                mostrarError("Correo o contraseña incorrectos.");
            } else if (code === "auth/invalid-email") {
                mostrarError("El correo electrónico no es válido.");
            } else if (code === "auth/user-disabled") {
                mostrarError("Esta cuenta ha sido deshabilitada.");
            } else if (code === "auth/too-many-requests") {
                mostrarAviso("Demasiados intentos. Inténtalo más tarde.");
            } else {
                console.error(err);
                mostrarError("Error al iniciar sesión.");
            }
        } finally {
            dispatch(clearAuthBlocked());
            dispatch(hideLoading());
        }
    };

    const handlePasswordReset = async () => {
        if (!email) {
            mostrarInfo("Introduce tu correo electrónico para recuperar la contraseña.");
            return;
        }
        dispatch(showLoading());
        try {
            await requestPasswordResetFn({ email });
            mostrarInfo(
                "Si el correo existe, recibirás un email para restablecer tu contraseña."
            );
        } catch (err) {
            console.error(err);
            mostrarError("No se pudo iniciar la recuperación de contraseña.");
        } finally {
            dispatch(hideLoading());
        }
    };


    return (
        <form onSubmit={handleLogin} className="auth-form">
            <h2>Iniciar sesión</h2>

            <TextField
                type="email"
                label="Correo electrónico"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                margin="normal"
            />

            <TextField
                type="password"
                label="Contraseña"
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                margin="normal"
                style={{ marginTop: "10px" }}
            />
            <button type="submit">Entrar</button>

            {showResetHint && (
                <p className="mb-0"
                    style={{
                        marginTop: "10px",
                        fontSize: "13px",
                        color: "#666",
                        textAlign: "center",
                    }}
                >
                    ¿Has olvidado tu contraseña?{" "}
                    <span
                        onClick={handlePasswordReset}
                        style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            margin: 0,
                            color: "#1976d2",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontSize: "13px",
                            fontFamily: "inherit",
                        }}
                    >
                        Restablécela aquí
                    </span>
                    .
                </p>
            )}
        </form>
    );
};

export default LoginForm;
